import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import api from '../services/api';
import { useToast } from '../hooks/useToast';

const إدارةالطلبات = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const { showToast, ToastContainer } = useToast();

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    paymentStatus: '',
    restaurantId: '',
    userId: '',
    fromDate: '',
    toDate: '',
    search: '',
  });

  useEffect(() => {
    fetchOrders();
    fetchStatistics();
  }, [page, filters]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
      });

      if (filters.status) params.append('status', filters.status);
      if (filters.paymentStatus) params.append('paymentStatus', filters.paymentStatus);
      if (filters.restaurantId) params.append('restaurantId', filters.restaurantId);
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.search) params.append('search', filters.search);

      const response = await api.getOrders({
        page: page.toString(),
        limit: '20',
        ...filters
      });
      
      if (response && response.items) {
        setOrders(response.items);
        setTotalPages(response.totalPages || 1);
        setTotal(response.total || 0);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      showToast('حدث خطأ في جلب الطلبات', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      setLoadingStats(true);
      const response = await api.getOrderStatistics({
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        restaurantId: filters.restaurantId || undefined,
      });
      setStatistics(response);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchOrderDetails = async (id) => {
    try {
      setLoadingDetails(true);
      const response = await api.getOrderById(id);
      setOrderDetails(response);
    } catch (error) {
      console.error('Error fetching order details:', error);
      showToast('حدث خطأ في جلب تفاصيل الطلب', 'error');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
    fetchOrderDetails(order.id);
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    if (!window.confirm('هل أنت متأكد من تحديث حالة الطلب؟')) {
      return;
    }

    try {
      await api.updateOrderStatus(orderId, newStatus);
      showToast('تم تحديث حالة الطلب بنجاح', 'success');
      fetchOrders();
      if (showDetailsModal && orderDetails && orderDetails.id === orderId) {
        fetchOrderDetails(orderId);
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      showToast('حدث خطأ في تحديث حالة الطلب', 'error');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(price || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDING: '#FFA500',
      CONFIRMED: '#4A90E2',
      DELIVERED: '#3FB984',
      CANCELED: '#E96A3A',
    };
    return colors[status] || '#666A7A';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      UNPAID: '#E96A3A',
      PENDING: '#FFA500',
      AUTHORIZED: '#4A90E2',
      PAID: '#3FB984',
      FAILED: '#E96A3A',
      REFUNDED: '#666A7A',
    };
    return colors[status] || '#666A7A';
  };

  const getStatusLabel = (status) => {
    const labels = {
      PENDING: 'قيد الانتظار',
      CONFIRMED: 'مؤكد',
      DELIVERED: 'تم التوصيل',
      CANCELED: 'ملغي',
    };
    return labels[status] || status;
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      UNPAID: 'غير مدفوع',
      PENDING: 'قيد الدفع',
      AUTHORIZED: 'مصرح',
      PAID: 'مدفوع',
      FAILED: 'فشل',
      REFUNDED: 'مسترد',
    };
    return labels[status] || status;
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: '#2B2F3F',
      overflow: 'hidden',
      direction: 'rtl'
    }}>
      <Header />
      <div style={{ 
        flex: 1, 
        padding: '32px', 
        backgroundColor: '#2B2F3F', 
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        boxSizing: 'border-box'
      }}>
        {/* Page Title */}
        <h1 style={{
          fontFamily: 'El Messiri, sans-serif',
          fontSize: '24px',
          fontWeight: 700,
          color: '#FFFFFF',
          margin: '0 0 8px 0',
          textAlign: 'right'
        }}>
          إدارة الطلبات
        </h1>

        {/* Statistics Cards */}
        {statistics && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '16px'
          }}>
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid rgba(233, 106, 58, 0.15)',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)'
            }}>
              <div style={{
                fontFamily: 'Cairo, sans-serif',
                fontSize: '12px',
                color: '#666A7A',
                marginBottom: '8px'
              }}>
                إجمالي الطلبات
              </div>
              <div style={{
                fontFamily: 'El Messiri, sans-serif',
                fontSize: '24px',
                fontWeight: 700,
                color: '#2B2F3F'
              }}>
                {statistics.totalOrders?.toLocaleString('ar-SA') || 0}
              </div>
            </div>
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid rgba(233, 106, 58, 0.15)',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)'
            }}>
              <div style={{
                fontFamily: 'Cairo, sans-serif',
                fontSize: '12px',
                color: '#666A7A',
                marginBottom: '8px'
              }}>
                إجمالي الإيرادات
              </div>
              <div style={{
                fontFamily: 'El Messiri, sans-serif',
                fontSize: '24px',
                fontWeight: 700,
                color: '#2B2F3F'
              }}>
                {formatPrice(statistics.totalRevenue)}
              </div>
            </div>
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid rgba(233, 106, 58, 0.15)',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)'
            }}>
              <div style={{
                fontFamily: 'Cairo, sans-serif',
                fontSize: '12px',
                color: '#666A7A',
                marginBottom: '8px'
              }}>
                متوسط قيمة الطلب
              </div>
              <div style={{
                fontFamily: 'El Messiri, sans-serif',
                fontSize: '24px',
                fontWeight: 700,
                color: '#2B2F3F'
              }}>
                {formatPrice(statistics.averageOrderValue)}
              </div>
            </div>
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid rgba(233, 106, 58, 0.15)',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)'
            }}>
              <div style={{
                fontFamily: 'Cairo, sans-serif',
                fontSize: '12px',
                color: '#666A7A',
                marginBottom: '8px'
              }}>
                طلبات اليوم
              </div>
              <div style={{
                fontFamily: 'El Messiri, sans-serif',
                fontSize: '24px',
                fontWeight: 700,
                color: '#2B2F3F'
              }}>
                {statistics.todayOrders?.toLocaleString('ar-SA') || 0}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid rgba(233, 106, 58, 0.15)',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px'
          }}>
            <input
              type="text"
              placeholder="بحث (اسم مطعم، عميل، بريد إلكتروني)"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              style={{
                padding: '10px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(102, 106, 122, 0.22)',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              dir="rtl"
            />
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{
                padding: '10px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(102, 106, 122, 0.22)',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
                cursor: 'pointer'
              }}
              dir="rtl"
            >
              <option value="">جميع الحالات</option>
              <option value="PENDING">قيد الانتظار</option>
              <option value="CONFIRMED">مؤكد</option>
              <option value="PREPARING">قيد التحضير</option>
              <option value="OUT_FOR_DELIVERY">في الطريق</option>
              <option value="DELIVERED">تم التوصيل</option>
              <option value="CANCELED">ملغي</option>
            </select>
            <select
              value={filters.paymentStatus}
              onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
              style={{
                padding: '10px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(102, 106, 122, 0.22)',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
                cursor: 'pointer'
              }}
              dir="rtl"
            >
              <option value="">جميع حالات الدفع</option>
              <option value="UNPAID">غير مدفوع</option>
              <option value="PENDING">قيد الدفع</option>
              <option value="AUTHORIZED">مصرح</option>
              <option value="PAID">مدفوع</option>
              <option value="FAILED">فشل</option>
              <option value="REFUNDED">مسترد</option>
            </select>
            <input
              type="date"
              placeholder="من تاريخ"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              style={{
                padding: '10px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(102, 106, 122, 0.22)',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            <input
              type="date"
              placeholder="إلى تاريخ"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              style={{
                padding: '10px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(102, 106, 122, 0.22)',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            <input
              type="text"
              placeholder="معرّف المطعم (اختياري)"
              value={filters.restaurantId}
              onChange={(e) => handleFilterChange('restaurantId', e.target.value)}
              style={{
                padding: '10px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(102, 106, 122, 0.22)',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              dir="rtl"
            />
            <input
              type="text"
              placeholder="معرّف العميل (اختياري)"
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
              style={{
                padding: '10px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(102, 106, 122, 0.22)',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              dir="rtl"
            />
            <button
              onClick={() => {
                setFilters({
                  status: '',
                  paymentStatus: '',
                  restaurantId: '',
                  userId: '',
                  fromDate: '',
                  toDate: '',
                  search: '',
                });
                setPage(1);
              }}
              style={{
                padding: '10px 20px',
                borderRadius: '12px',
                border: '1px solid rgba(102, 106, 122, 0.22)',
                backgroundColor: '#FFFFFF',
                color: '#666A7A',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '14px',
                cursor: 'pointer',
                boxSizing: 'border-box'
              }}
            >
              إعادة تعيين
            </button>
          </div>
        </div>

        {/* Orders Table */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid rgba(233, 106, 58, 0.15)',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
          flex: 1,
          overflow: 'auto',
          boxSizing: 'border-box'
        }}>
          {loading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px',
              color: '#666A7A',
              fontFamily: 'Cairo, sans-serif'
            }}>
              جاري التحميل...
            </div>
          ) : orders.length === 0 ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px',
              color: '#666A7A',
              fontFamily: 'Cairo, sans-serif'
            }}>
              لا توجد طلبات
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontFamily: 'Cairo, sans-serif'
                }}>
                  <thead>
                    <tr style={{
                      borderBottom: '2px solid #E5E7EB'
                    }}>
                      <th style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#2B2F3F'
                      }}>رقم الطلب</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#2B2F3F'
                      }}>العميل</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#2B2F3F'
                      }}>المطعم</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#2B2F3F'
                      }}>المبلغ</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#2B2F3F'
                      }}>الحالة</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#2B2F3F'
                      }}>حالة الدفع</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#2B2F3F'
                      }}>التاريخ</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#2B2F3F'
                      }}>الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id} style={{
                        borderBottom: '1px solid #E5E7EB',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleViewDetails(order)}
                      >
                        <td style={{
                          padding: '12px',
                          fontSize: '14px',
                          color: '#2B2F3F'
                        }}>#{order.id.substring(0, 8)}</td>
                        <td style={{
                          padding: '12px',
                          fontSize: '14px',
                          color: '#2B2F3F'
                        }}>
                          {order.user ? `${order.user.firstName} ${order.user.lastName}` : '-'}
                        </td>
                        <td style={{
                          padding: '12px',
                          fontSize: '14px',
                          color: '#2B2F3F'
                        }}>
                          {order.restaurant?.name || '-'}
                        </td>
                        <td style={{
                          padding: '12px',
                          fontSize: '14px',
                          color: '#2B2F3F',
                          fontWeight: 600
                        }}>
                          {formatPrice(order.totalPrice)}
                        </td>
                        <td style={{
                          padding: '12px',
                          fontSize: '14px'
                        }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '8px',
                            backgroundColor: getStatusColor(order.status) + '20',
                            color: getStatusColor(order.status),
                            fontFamily: 'Cairo, sans-serif',
                            fontSize: '12px',
                            fontWeight: 600
                          }}>
                            {getStatusLabel(order.status)}
                          </span>
                        </td>
                        <td style={{
                          padding: '12px',
                          fontSize: '14px'
                        }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '8px',
                            backgroundColor: getPaymentStatusColor(order.paymentStatus) + '20',
                            color: getPaymentStatusColor(order.paymentStatus),
                            fontFamily: 'Cairo, sans-serif',
                            fontSize: '12px',
                            fontWeight: 600
                          }}>
                            {getPaymentStatusLabel(order.paymentStatus)}
                          </span>
                        </td>
                        <td style={{
                          padding: '12px',
                          fontSize: '14px',
                          color: '#666A7A'
                        }}>
                          {formatDate(order.createdAt)}
                        </td>
                        <td style={{
                          padding: '12px',
                          fontSize: '14px'
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(order);
                            }}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '8px',
                              border: '1px solid #E96A3A',
                              backgroundColor: 'transparent',
                              color: '#E96A3A',
                              fontFamily: 'Cairo, sans-serif',
                              fontSize: '12px',
                              cursor: 'pointer',
                              marginLeft: '8px'
                            }}
                          >
                            التفاصيل
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '24px',
                  paddingTop: '24px',
                  borderTop: '1px solid #E5E7EB',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '13px',
                    color: '#666A7A'
                  }}>
                    عرض {orders.length} من {total} طلب
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center'
                  }}>
                    <button
                      onClick={() => setPage(1)}
                      disabled={page === 1}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid rgba(102, 106, 122, 0.22)',
                        backgroundColor: page === 1 ? '#f5f5f5' : '#FFFFFF',
                        color: page === 1 ? '#999' : '#666A7A',
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '13px',
                        cursor: page === 1 ? 'not-allowed' : 'pointer',
                        opacity: page === 1 ? 0.6 : 1
                      }}
                    >
                      الأولى
                    </button>
                    <button
                      onClick={() => setPage(prev => Math.max(1, prev - 1))}
                      disabled={page === 1}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(102, 106, 122, 0.22)',
                        backgroundColor: page === 1 ? '#f5f5f5' : '#FFFFFF',
                        color: page === 1 ? '#999' : '#666A7A',
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '14px',
                        cursor: page === 1 ? 'not-allowed' : 'pointer',
                        opacity: page === 1 ? 0.6 : 1
                      }}
                    >
                      السابق
                    </button>
                    <div style={{
                      display: 'flex',
                      gap: '4px',
                      alignItems: 'center',
                      padding: '0 8px'
                    }}>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (page <= 3) {
                          pageNum = i + 1;
                        } else if (page >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = page - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setPage(pageNum)}
                            style={{
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: 'none',
                              backgroundColor: page === pageNum ? '#E96A3A' : 'transparent',
                              color: page === pageNum ? '#FFFFFF' : '#666A7A',
                              fontFamily: 'Cairo, sans-serif',
                              fontSize: '14px',
                              fontWeight: page === pageNum ? 600 : 400,
                              cursor: 'pointer',
                              minWidth: '36px'
                            }}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={page === totalPages}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(102, 106, 122, 0.22)',
                        backgroundColor: page === totalPages ? '#f5f5f5' : '#FFFFFF',
                        color: page === totalPages ? '#999' : '#666A7A',
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '14px',
                        cursor: page === totalPages ? 'not-allowed' : 'pointer',
                        opacity: page === totalPages ? 0.6 : 1
                      }}
                    >
                      التالي
                    </button>
                    <button
                      onClick={() => setPage(totalPages)}
                      disabled={page === totalPages}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid rgba(102, 106, 122, 0.22)',
                        backgroundColor: page === totalPages ? '#f5f5f5' : '#FFFFFF',
                        color: page === totalPages ? '#999' : '#666A7A',
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '13px',
                        cursor: page === totalPages ? 'not-allowed' : 'pointer',
                        opacity: page === totalPages ? 0.6 : 1
                      }}
                    >
                      الأخيرة
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Order Details Modal */}
      {showDetailsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px',
          boxSizing: 'border-box'
        }}
        onClick={() => {
          setShowDetailsModal(false);
          setOrderDetails(null);
        }}
        >
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxSizing: 'border-box',
            direction: 'rtl'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{
                fontFamily: 'El Messiri, sans-serif',
                fontSize: '20px',
                fontWeight: 700,
                color: '#2B2F3F',
                margin: 0
              }}>
                تفاصيل الطلب
              </h2>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setOrderDetails(null);
                }}
                style={{
                  padding: '8px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontSize: '24px',
                  color: '#666A7A'
                }}
              >
                ×
              </button>
            </div>

            {loadingDetails ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '40px',
                color: '#666A7A',
                fontFamily: 'Cairo, sans-serif'
              }}>
                جاري التحميل...
              </div>
            ) : orderDetails ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px'
              }}>
                {/* Order Info */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '16px'
                }}>
                  <div>
                    <div style={{
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '12px',
                      color: '#666A7A',
                      marginBottom: '4px'
                    }}>
                      رقم الطلب
                    </div>
                    <div style={{
                      fontFamily: 'El Messiri, sans-serif',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#2B2F3F'
                    }}>
                      #{orderDetails.id.substring(0, 8)}
                    </div>
                  </div>
                  <div>
                    <div style={{
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '12px',
                      color: '#666A7A',
                      marginBottom: '4px'
                    }}>
                      الحالة
                    </div>
                    <div>
                      <select
                        value={orderDetails.status}
                        onChange={(e) => handleUpdateStatus(orderDetails.id, e.target.value)}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid rgba(102, 106, 122, 0.22)',
                          fontFamily: 'Cairo, sans-serif',
                          fontSize: '14px',
                          outline: 'none',
                          cursor: 'pointer',
                          backgroundColor: getStatusColor(orderDetails.status) + '20',
                          color: getStatusColor(orderDetails.status),
                          fontWeight: 600
                        }}
                      >
                        <option value="PENDING">قيد الانتظار</option>
                        <option value="CONFIRMED">مؤكد</option>
                        <option value="DELIVERED">تم التوصيل</option>
                        <option value="CANCELED">ملغي</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <div style={{
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '12px',
                      color: '#666A7A',
                      marginBottom: '4px'
                    }}>
                      حالة الدفع
                    </div>
                    <div style={{
                      fontFamily: 'El Messiri, sans-serif',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: getPaymentStatusColor(orderDetails.paymentStatus)
                    }}>
                      {getPaymentStatusLabel(orderDetails.paymentStatus)}
                    </div>
                  </div>
                  <div>
                    <div style={{
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '12px',
                      color: '#666A7A',
                      marginBottom: '4px'
                    }}>
                      المبلغ الإجمالي
                    </div>
                    <div style={{
                      fontFamily: 'El Messiri, sans-serif',
                      fontSize: '16px',
                      fontWeight: 700,
                      color: '#2B2F3F'
                    }}>
                      {formatPrice(orderDetails.totalPrice)}
                    </div>
                  </div>
                </div>

                {/* User Info */}
                {orderDetails.user && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '12px'
                  }}>
                    <h3 style={{
                      fontFamily: 'El Messiri, sans-serif',
                      fontSize: '16px',
                      fontWeight: 700,
                      color: '#2B2F3F',
                      marginBottom: '12px'
                    }}>
                      معلومات العميل
                    </h3>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '12px',
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '14px',
                      color: '#2B2F3F'
                    }}>
                      <div>
                        <span style={{ color: '#666A7A' }}>الاسم: </span>
                        {orderDetails.user.firstName} {orderDetails.user.lastName}
                      </div>
                      <div>
                        <span style={{ color: '#666A7A' }}>البريد: </span>
                        {orderDetails.user.email}
                      </div>
                      {orderDetails.user.phone && (
                        <div>
                          <span style={{ color: '#666A7A' }}>الهاتف: </span>
                          {orderDetails.user.phone}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Restaurant Info */}
                {orderDetails.restaurant && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '12px'
                  }}>
                    <h3 style={{
                      fontFamily: 'El Messiri, sans-serif',
                      fontSize: '16px',
                      fontWeight: 700,
                      color: '#2B2F3F',
                      marginBottom: '12px'
                    }}>
                      معلومات المطعم
                    </h3>
                    <div style={{
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '14px',
                      color: '#2B2F3F'
                    }}>
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#666A7A' }}>الاسم: </span>
                        {orderDetails.restaurant.name}
                      </div>
                      {orderDetails.restaurant.owner && (
                        <div>
                          <span style={{ color: '#666A7A' }}>المالك: </span>
                          {orderDetails.restaurant.owner.firstName} {orderDetails.restaurant.owner.lastName}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Delivery Info */}
                <div style={{
                  padding: '16px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '12px'
                }}>
                  <h3 style={{
                    fontFamily: 'El Messiri, sans-serif',
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#2B2F3F',
                    marginBottom: '12px'
                  }}>
                    معلومات التوصيل
                  </h3>
                  <div style={{
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    color: '#2B2F3F'
                  }}>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ color: '#666A7A' }}>نوع التوصيل: </span>
                      {orderDetails.deliveryType === 'DELIVERY' ? 'توصيل' : 'استلام من المطعم'}
                    </div>
                    {orderDetails.address && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#666A7A' }}>العنوان: </span>
                        {orderDetails.address}
                      </div>
                    )}
                    {orderDetails.notes && (
                      <div>
                        <span style={{ color: '#666A7A' }}>ملاحظات: </span>
                        {orderDetails.notes}
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Items */}
                {orderDetails.items && orderDetails.items.length > 0 && (
                  <div>
                    <h3 style={{
                      fontFamily: 'El Messiri, sans-serif',
                      fontSize: '16px',
                      fontWeight: 700,
                      color: '#2B2F3F',
                      marginBottom: '12px'
                    }}>
                      الوجبات المطلوبة
                    </h3>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      {orderDetails.items.map((item) => (
                        <div key={item.id} style={{
                          padding: '16px',
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          borderRadius: '12px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div>
                            <div style={{
                              fontFamily: 'El Messiri, sans-serif',
                              fontSize: '16px',
                              fontWeight: 600,
                              color: '#2B2F3F',
                              marginBottom: '4px'
                            }}>
                              {item.meal?.name || 'وجبة محذوفة'}
                            </div>
                            <div style={{
                              fontFamily: 'Cairo, sans-serif',
                              fontSize: '14px',
                              color: '#666A7A'
                            }}>
                              الكمية: {item.quantity} × {formatPrice(item.meal?.price || 0)}
                            </div>
                            {item.note && (
                              <div style={{
                                fontFamily: 'Cairo, sans-serif',
                                fontSize: '12px',
                                color: '#666A7A',
                                marginTop: '4px',
                                fontStyle: 'italic'
                              }}>
                                ملاحظة: {item.note}
                              </div>
                            )}
                          </div>
                          <div style={{
                            fontFamily: 'El Messiri, sans-serif',
                            fontSize: '18px',
                            fontWeight: 700,
                            color: '#2B2F3F'
                          }}>
                            {formatPrice(item.price)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Order Dates */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '16px',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  color: '#666A7A'
                }}>
                  <div>
                    <span style={{ color: '#666A7A' }}>تاريخ الإنشاء: </span>
                    {formatDate(orderDetails.createdAt)}
                  </div>
                  {orderDetails.updatedAt && (
                    <div>
                      <span style={{ color: '#666A7A' }}>آخر تحديث: </span>
                      {formatDate(orderDetails.updatedAt)}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default إدارةالطلبات;

